import { useMemo } from 'react';

import { OnboardingStep } from '../types';

// Section + option keys come from the backend (search_filter_metadata) and
// back the data-tour="filter-section-<name>" / data-tour-option="<name>"
// attributes plus the expand action key.
const RATING_STATUS_SECTION = 'rating_status';
const MARKET_INDICATOR_SECTION = 'market_indicator';
const NOT_RATED_OPTION = 'not_rated';
const CCP_GROUP = 'ccp';
const CCP_APPROVED_OPTION = 'ccp_approved';

// Tour-action keys components register via useTourAction. Steps reference them
// here; the runner awaits each in order before driving driver.js. New tours
// add their own action keys + the components that own the state register them.
const TOUR_ACTIONS = {
  openDrawer: 'unrated:open-drawer',
  expandRatingStatus: `filter:expand:${RATING_STATUS_SECTION}`,
  expandMarketIndicator: `filter:expand:${MARKET_INDICATOR_SECTION}`,
  expandCcpGroup: `filter:expand-child:${MARKET_INDICATOR_SECTION}:${CCP_GROUP}`,
  // Auto-tick the prior step's target on advance, so the filter is applied
  // even if the user clicked somewhere other than the exact checkbox.
  checkNotRated: `filter:check:${RATING_STATUS_SECTION}:${NOT_RATED_OPTION}`,
  checkCcpApproved: `filter:check:${MARKET_INDICATOR_SECTION}:${CCP_APPROVED_OPTION}`,
};

/** Distance from the highlighted target's left edge to the drawer's left edge
 *  + 20px padding — pushes the popover's right edge clear of the drawer so it
 *  lands over the table area (matches the Figma frame). Read-only DOM
 *  measurement; the underlying state changes go through React via tour actions. */
const offsetToClearDrawer = (): number => {
  const drawer = document.querySelector<HTMLElement>('.MuiDrawer-root [role="dialog"]');
  const target = document.querySelector<HTMLElement>(`[data-tour-option="${NOT_RATED_OPTION}"]`);
  if (!drawer || !target) return 80;
  return Math.max(
    20,
    target.getBoundingClientRect().left - drawer.getBoundingClientRect().left + 20
  );
};

export const useUnratedOnboardingSteps = (): OnboardingStep[] =>
  useMemo<OnboardingStep[]>(
    () => [
      {
        // "Not rated" checkbox row inside the rating_status accordion.
        // beforeActions: opens the drawer, then expands the accordion (each
        // action waits for the corresponding animation to settle). The
        // popoverOffset pushes the popover past the drawer's left edge.
        element: `[data-tour="filter-section-${RATING_STATUS_SECTION}"] [data-tour-option="${NOT_RATED_OPTION}"]`,
        side: 'left',
        title: 'Select "Not rated" to explore projects we haven\'t rated yet.',
        body: '',
        show: true,
        interactive: true,
        beforeActions: [TOUR_ACTIONS.openDrawer, TOUR_ACTIONS.expandRatingStatus],
        popoverOffset: offsetToClearDrawer,
      },
      {
        // "Approved" row inside the CCP group inside the market_indicator
        // section. beforeActions: expand the section accordion, then expand
        // the CCP child-group so the ccp_approved row mounts in DOM.
        element: `[data-tour="filter-section-${MARKET_INDICATOR_SECTION}"] [data-tour-option="${CCP_APPROVED_OPTION}"]`,
        side: 'left',
        title:
          "We've mapped the eligibility status for rated and unrated projects. See which projects qualify for CCP, Article 6, and CORSIA",
        body: '',
        show: true,
        interactive: true,
        beforeActions: [
          // Ensure step 1's target stays ticked even if the user clicked elsewhere.
          TOUR_ACTIONS.checkNotRated,
          TOUR_ACTIONS.expandMarketIndicator,
          TOUR_ACTIONS.expandCcpGroup,
        ],
      },
      {
        element: '[data-tour="filter-done"]',
        side: 'top',
        // Right-align the popover with the Done button, but center the highlight
        // dot + tail on the button rather than pinning them to its corner.
        align: 'end',
        centerDot: true,
        // Curly quotes match the Figma typography (var(--text/primary)).
        title: 'Click “Done” to see the projects that match your criteria.',
        body: '',
        show: true,
        interactive: true,
        beforeActions: [TOUR_ACTIONS.checkCcpApproved],
      },
    ],
    []
  );
